import { EAnalysisStatus } from '@sharedInterfaces/IEmployeeTalkState';
import { ETemplateCategory } from '@sharedInterfaces/IEmployeeTalkTemplate';
import getEmployee from '@src/APIs/graphQl/Employee/getEmployee';
import getEmployeeTalk from '@src/APIs/graphQl/EmployeeTalk/getEmployeeTalk';
import { useSecureEmployeeTalkState } from '@src/App/Hooks/useSecureEmployeeTalkState';
import type { UseSecureEmployeeTalkStateReturn } from '@src/App/Hooks/useSecureEmployeeTalkState/types';
import { fireworkSalvo } from '@src/App/Pages/MyProfilePage/fireworkSalvo';
import type ISkill from '@src/interfaces/ISkill';
import type EmployeeTalk from '@src/Objects/EmployeeTalk';
import type { AppState } from '@store/store';
import React, { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEmployeeTalkState, INameGenderData } from '../../../../../sharedInterfaces/IEmployeeTalkState';
import type IEmployeeTalkTemplate from '../../../../../sharedInterfaces/IEmployeeTalkTemplate';

// UI state for provider
interface IProviderUIState {
    showAgent: boolean;
}

// Helper to check if manual mode is enabled
const isManualMode = (state: IEmployeeTalkState | null): boolean => {
    if (!state) return false;
    const cvUploadComponent = Object.values(state.components).find(c => c.componentType === 'cvUpload');
    return (cvUploadComponent?.value as { manualContinue?: boolean })?.manualContinue === true;
};

// Provider context interface - extends hook with UI-specific functionality
interface IEmployeeTalkContext extends UseSecureEmployeeTalkStateReturn {
    // Entity and template data
    employeeTalk: EmployeeTalk | null;
    template: IEmployeeTalkTemplate | null;
    isOnboarding: boolean;
    isManualMode: boolean;
    // Target employee data
    targetEmployeeSkills: ISkill[];
    // UI state
    showAgent: boolean;
    // Helper functions
    getEmployeeName: (id: number) => string;
    formatDate: (date?: string) => string;
    // Legacy compatibility
    secureState: IEmployeeTalkState | null;
    progress: number;
    canProceedToNextStep: boolean;
    availableSteps: number[];
    handleNext: () => Promise<void>;
    handleBack: () => Promise<void>;
    handleStepClick: (step: number) => Promise<void>;
    /** @deprecated Use updatePersonalData() instead */
    updatePersonalInfo: (componentId: string, field: string, value: string) => Promise<void>;
}

const EmployeeTalkContext = createContext<IEmployeeTalkContext | null>(null);

interface ETTEmployeeTalkProviderProps {
    employeeTalkId: number;
    children: ReactNode;
}

/**
 * ETT (EmployeeTalk Template) Provider Component
 *
 * Provides a hybrid state management system that combines:
 * - WebSocket real-time state updates (primary)
 * - Redux entity state fallback (secondary)
 * - GraphQL initial data loading
 *
 * Features:
 * - React Strict Mode compatible
 * - Automatic fallback to entity state if WebSocket fails
 * - Type-safe component data access
 * - Event-driven state management with backend validation
 *
 * @param employeeTalkId - ID of the EmployeeTalk to provide context for
 * @param children - Child components that will have access to the context
 */
export const ETTEmployeeTalkProvider: React.FC<ETTEmployeeTalkProviderProps> = ({ employeeTalkId, children }) => {
    // console.log('🚀 ETTEmployeeTalkProvider MOUNT:', employeeTalkId);

    // Redux selectors
    const employeeTalks = useSelector((state: AppState) => state.employeeTalks);
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    // Local loading state for GraphQL
    const [graphqlLoading, setGraphqlLoading] = useState(false);
    const [graphqlError, setGraphqlError] = useState<string | null>(null);

    const employeeTalk = employeeTalks.find((t) => t.id === employeeTalkId) || null;

    // console.log('📊 Redux employeeTalks:', employeeTalks.length, 'items');
    const template: IEmployeeTalkTemplate | null = employeeTalk?.templateData || null;
    const isOnboarding = template?.templateCategory === ETemplateCategory.ONBOARDING;

    // Load EmployeeTalk via GraphQL if not in Redux
    useEffect(() => {
        if (!employeeTalk && !graphqlLoading && !graphqlError) {
            setGraphqlLoading(true);
            getEmployeeTalk(employeeTalkId)
                .then(() => {
                    setGraphqlLoading(false);
                })
                .catch((error) => {
                    setGraphqlError(error.message || 'Failed to load EmployeeTalk');
                    setGraphqlLoading(false);
                });
        }
    }, [employeeTalkId, employeeTalk, graphqlLoading, graphqlError]);

    // Load target employee's full data (including skills) once
    const [targetEmployeeSkills, setTargetEmployeeSkills] = useState<ISkill[]>([]);
    const [targetEmployeeLoaded, setTargetEmployeeLoaded] = useState(false);

    useEffect(() => {
        if (employeeTalk?.employeeId && !targetEmployeeLoaded) {
            setTargetEmployeeLoaded(true);
            getEmployee(employeeTalk.employeeId)
                .then((employee) => {
                    setTargetEmployeeSkills(employee.skills || []);
                })
                .catch((err) => {
                    console.error('Failed to load target employee skills:', err);
                    setTargetEmployeeSkills([]);
                });
        }
    }, [employeeTalk?.employeeId, targetEmployeeLoaded]);

    // Use secure state hook with event-driven architecture
    const hookState = useSecureEmployeeTalkState(employeeTalkId);
    const { talkState, loading: webSocketLoading, error: webSocketError } = hookState;

    /**
     * HYBRID STATE MANAGEMENT - Prevents UI failure when WebSocket drops
     *
     * ARCHITECTURE: Layered fallback system for maximum reliability
     * Priority order (first available wins):
     * 1. WebSocket state (talkState) - Real-time updates, primary source
     * 2. Entity state (employeeTalk?.talkState) - Fallback from Redux/GraphQL
     * 3. null - Loading state
     *
     * BENEFIT: UI continues working even if WebSocket connection fails
     * IMPLEMENTATION: Simple || operator provides automatic fallback
     */
    const activeState = talkState || employeeTalk?.talkState || null;
    const loading = graphqlLoading && !employeeTalk; // Only loading if no entity
    const error = graphqlError || webSocketError;

    // FALLBACK DETECTION: Warn when using entity state instead of WebSocket state
    // Helps identify WebSocket connection issues during development
    // In production, this fallback is transparent to users
    if (!talkState && !webSocketLoading && employeeTalk?.talkState) {
        console.warn('⚠️ Using entity talkState fallback - WebSocket may not be working');
    }

    // Debug logging (only for errors)
    React.useEffect(() => {
        if (error) {
            console.error('❌ Provider Error:', error);
        }
    }, [error]);

    // Listen for approval updates and refresh entity
    useEffect(() => {
        const handleApprovalUpdate = () => {
            if (employeeTalk) {
                getEmployeeTalk(employeeTalkId).catch((err) => console.error('Failed to refresh after approval:', err));
            }
        };
        window.addEventListener('employeeTalkApprovalUpdate', handleApprovalUpdate as EventListener);
        return () => window.removeEventListener('employeeTalkApprovalUpdate', handleApprovalUpdate as EventListener);
    }, [employeeTalkId, employeeTalk]);

    // Navigation values from active state (reactive to entity changes)
    const currentEmployee = useSelector((state: AppState) => state.employee);
    const activeCurrentStep = activeState?.navigation.currentStep || 0;
    const isManager = employeeTalk?.meetingPartnerId === currentEmployee.id;
    const activeAvailableSteps = isManager 
        ? (activeState?.navigation.availableStepsManager || activeState?.navigation.availableSteps || [])
        : (activeState?.navigation.availableSteps || []);
    
    // Debug logging for navigation
    React.useEffect(() => {
        console.log('🔍 Navigation Debug:', {
            currentEmployeeId: currentEmployee.id,
            employeeId: employeeTalk?.employeeId,
            meetingPartnerId: employeeTalk?.meetingPartnerId,
            isManager,
            availableSteps: activeState?.navigation.availableSteps,
            availableStepsManager: activeState?.navigation.availableStepsManager,
            activeAvailableSteps,
        });
    }, [currentEmployee.id, employeeTalk, isManager, activeState, activeAvailableSteps]);
    const prevStepRef = useRef<number | null>(null);
    const hasAutoApprovedRef = useRef(false);

    // Auto-approve and trigger fireworks when navigating to completion page (step 8)
    useEffect(() => {
        const isNavigatingToCompletion = prevStepRef.current === 7 && activeCurrentStep === 8;
        const isNotCompleted = !employeeTalk?.employeeConfirmed;

        if (isNavigatingToCompletion && isNotCompleted && !hasAutoApprovedRef.current) {
            hasAutoApprovedRef.current = true;
            // Trigger fireworks
            fireworkSalvo();
            // Auto-approve as employee
            if (employeeTalk && !employeeTalk.employeeConfirmed) {
                hookState.approveEmployeeTalk('employee').catch((err) => {
                    console.error('Failed to auto-approve:', err);
                });
            }
        }
        prevStepRef.current = activeCurrentStep;
    }, [activeCurrentStep, employeeTalk, hookState]);
    // Reset auto-approval flag when leaving completion page
    useEffect(() => {
        if (activeCurrentStep !== 8) {
            hasAutoApprovedRef.current = false;
        }
    }, [activeCurrentStep]);

    const activeCanNavigateToStep = React.useCallback(
        (step: number): boolean => {
            return activeAvailableSteps.includes(step);
        },
        [activeAvailableSteps],
    );

    /**
     * Type-safe component data getter with reactive updates
     *
     * CRITICAL: entityStateVersion dependency ensures re-renders when entity state changes
     * WITHOUT this: Components using entity fallback wouldn't update when state changes
     * WITH this: All components automatically re-render when any state source updates
     *
     * Type safety: Generic <T> provides compile-time type checking for component data
     */
    const getComponentData = React.useCallback(
        <T,>(componentId: string): T | undefined => activeState?.components?.[componentId]?.value as T,
        [activeState],
    );

    // Local UI state
    const [uiState] = React.useState<IProviderUIState>({
        showAgent: true,
    });

    // Helper functions
    const getEmployeeName = (employeeId: number): string => {
        const employee = allEmployees.find((e) => e.id === employeeId);
        return employee ? employee.title : `ID: ${employeeId}`;
    };

    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    // Legacy compatibility methods
    const progress = template ? ((activeCurrentStep + 1) / template.pages.length) * 100 : 0;
    const canProceedToNextStep = activeAvailableSteps.includes(activeCurrentStep + 1);

    const handleNext = async (): Promise<void> => {
        const nextStep = activeCurrentStep + 1;
        if (canProceedToNextStep && template && nextStep < template.pages.length) {
            await hookState.navigateToStep(nextStep);
        }
    };

    const handleBack = async (): Promise<void> => {
        const prevStep = activeCurrentStep - 1;
        if (prevStep >= 0) {
            await hookState.navigateToStep(prevStep);
        }
    };

    const handleStepClick = async (stepIndex: number): Promise<void> => {
        if (activeCanNavigateToStep(stepIndex)) {
            await hookState.navigateToStep(stepIndex);
        }
    };

    // DEPRECATED: File upload is now handled entirely by backend
    // Backend automatically updates component state after successful upload
    // const uploadFile = async (componentId: string, stepIndex: number, attachment: { id: string; title: string }): Promise<void> => {
    //   const currentData = getComponentData(componentId) || {};
    //   await hookState.updateComponentData(componentId, {
    //     ...currentData,
    //     attachmentId: attachment.id,
    //     fileName: attachment.title
    //   });
    // };

    /** @deprecated Use updatePersonalData() instead for WebSocket-only updates */
    const updatePersonalInfo = async (componentId: string, field: string, value: string): Promise<void> => {
        console.warn('⚠️ updatePersonalInfo is deprecated. Use updatePersonalData() for WebSocket-only updates.');
        const currentData = getComponentData<INameGenderData>(componentId) || { firstName: '', lastName: '' };
        await hookState.updateComponentData(componentId, { ...currentData, [field]: value });
    };

    // Analysis functions (reactive to entity changes)
    const activeGetAnalysisData = React.useCallback(
        (field: 'skills' | 'certificates' | 'roles' | 'description'): unknown => {
            return activeState?.analysis[field]?.data;
        },
        [activeState],
    );

    const activeIsAnalysisComplete = React.useCallback((): boolean => {
        if (!activeState?.analysis) return false;
        return Object.entries(activeState.analysis).every(([key, field]) => {
            if (key === 'started' || key === 'attachmentId') return true; // Skip non-field properties
            if (typeof field === 'string' || typeof field === 'boolean') return true; // Skip string/boolean fields
            return field.status === EAnalysisStatus.COMPLETED || field.status === EAnalysisStatus.IDLE;
        });
    }, [activeState]);

    // Compute readonly status for current user
    const isReadOnlyForCurrentUser = React.useMemo(() => {
        if (!employeeTalk) return false;
        const isEmployee = currentEmployee.id === employeeTalk.employeeId;
        const isManager = currentEmployee.id === employeeTalk.meetingPartnerId;
        if (employeeTalk.employeeConfirmed && employeeTalk.partnerConfirmed) return true;
        if (isEmployee && employeeTalk.employeeConfirmed) return true;
        if (isManager && employeeTalk.partnerConfirmed) return true;
        return false;
    }, [employeeTalk, currentEmployee.id]);

    // Check if manual mode is enabled
    const manualMode = isManualMode(activeState);

    // Create context value by extending hook state with UI-specific functionality
    const contextValue: IEmployeeTalkContext = {
        // Spread all hook functionality
        ...hookState,
        // Override with hybrid state management
        talkState: activeState,
        currentStep: activeCurrentStep,
        canNavigateToStep: activeCanNavigateToStep,
        getComponentData,
        getAnalysisData: activeGetAnalysisData,
        isAnalysisComplete: activeIsAnalysisComplete,
        isReadOnlyForCurrentUser,
        // Add UI-specific functionality
        employeeTalk,
        template,
        isOnboarding,
        isManualMode: manualMode,
        targetEmployeeSkills,
        loading,
        error,
        showAgent: uiState.showAgent,
        getEmployeeName,
        formatDate,
        // Legacy compatibility
        secureState: activeState,
        progress,
        canProceedToNextStep,
        availableSteps: activeAvailableSteps,
        handleNext,
        handleBack,
        handleStepClick,
        updatePersonalInfo,
    };

    return <EmployeeTalkContext.Provider value={contextValue}>{children}</EmployeeTalkContext.Provider>;
};

export const useEmployeeTalk = () => {
    const context = useContext(EmployeeTalkContext);
    if (!context) {
        throw new Error('useEmployeeTalk must be used within ETTEmployeeTalkProvider');
    }
    return context;
};

export default ETTEmployeeTalkProvider;
